import { type Breakpoint } from '@/components/utility/size/show-on-size'
import { type PropsWithChildren, type ReactNode, Suspense } from 'react'

import { type SectionHeaderProps } from '@/components/utility/section-header'
import dynamic from 'next/dynamic'
import { Paginator } from './paginator'

const SectionHeader = dynamic(
  async () =>
    await import('@/components/utility/section-header').then(
      (mod) => mod.SectionHeader,
    ),
  {
    ssr: false,
  },
)

type BottomPaginatorStyle = 'bar' | 'dot'
interface PaginationListProps {
  elements: JSX.Element[]
  maxSizeForScroll?: Breakpoint
  title: string
  headingVariant?: string
  subHeading?: JSX.Element
  seeAllTitle?: string
  seeAllLink?: string
  showBottomPagination?: boolean
  showTopPagination?: boolean
  bottomPaginatorStyle?: BottomPaginatorStyle
  margin?: number
  repeaterMargin?: number
  isSkeleton: boolean
  spacing?: number
  totalPages: number
  itemPerPage: number
  gridClassName?: string
  rows?: number
  gridJustify?: string
  elementWidth?: number
  autoFlow?: string
  cursor: number
  headerProps?: SectionHeaderProps
  currentPage: number
  columns?: number
  searchParams?: Record<string, string>
}

interface PaginationInnerGridProps {
  numberOfPages: number
  currentPage: number
  rows?: number
  gridJustify?: string
  elementWidth?: number
  gridClassName?: string

  autoFlow?: string
  showBottomPagination?: boolean
  elements: JSX.Element[]
  searchParams?: Record<string, string>
  title?: string
}

interface ParamsForComponents {
  gridClassName: string
  elementWidth?: number | string
  rows?: number
  gridJustify?: string
  autoFlow?: string
}

const RepeaterContainer = ({
  gridClassName,
  children,
  ...props
}: ParamsForComponents & { children: ReactNode; 'data-testid'?: string }) => {
  return (
    <div
      data-testid={props['data-testid']}
      className={`grid ${gridClassName} gap-6`}
    >
      {children}
    </div>
  )
}

const OuterContainer = ({ children }: { children: ReactNode }) => (
  <div className='relative my-4'>{children}</div>
)

const TitleText = ({
  children,
  isSkeleton = false,
  headingVariant,
}: TitleProps & { children: ReactNode }) => {
  if (headingVariant === 'h1') {
    return (
      <h1
        className='mx-0 my-4 text-[32px] font-bold'
        data-testid={`pagination-list-title${isSkeleton ? '-skeleton' : ''}`}
      >
        {children}
      </h1>
    )
  }
  return (
    <h2
      className='mx-0 my-4 text-[32px] font-bold'
      data-testid={`pagination-list-title${isSkeleton ? '-skeleton' : ''}`}
    >
      {children}
    </h2>
  )
}

const NoResults = ({ children }: { children: ReactNode }) => (
  <div className='mx-0 my-2'>{children}</div>
)

const ListHeaderContainer = ({ children }: { children: ReactNode }) => (
  <div className='w-full max-w-[calc(100vw-32px)]'>{children}</div>
)

const TitleRow = ({ children }: { children: ReactNode }) => (
  <div className='flex w-full'>{children}</div>
)

type TitleProps = Pick<
  PaginationListProps,
  'isSkeleton' | 'title' | 'headingVariant'
>

const Title = ({ isSkeleton, title, headingVariant }: TitleProps) => {
  const titleVariant = headingVariant === 'h1' ? 'h1' : 'h2'
  return (
    <TitleText headingVariant={titleVariant} isSkeleton={isSkeleton} title=''>
      {title}
    </TitleText>
  )
}

type ListHeaderProps = PropsWithChildren<TitleProps> & {
  subHeading?: JSX.Element
}
const ListHeader = ({ children, subHeading, ...props }: ListHeaderProps) => (
  <ListHeaderContainer>
    <TitleRow>
      <Title {...props} />
      {children}
    </TitleRow>
    {subHeading}
  </ListHeaderContainer>
)

export const PaginationInnerGrid = ({
  numberOfPages,
  currentPage,
  gridClassName,
  elementWidth,
  rows,
  gridJustify,
  autoFlow,
  showBottomPagination,
  elements,
  searchParams,
}: PaginationInnerGridProps) => (
  <>
    <OuterContainer>
      <RepeaterContainer
        gridClassName={
          gridClassName ? `justify-center ${gridClassName}` : 'justify-center'
        }
        elementWidth={elementWidth}
        rows={rows}
        data-testid='pagination-grid-container'
        gridJustify={gridJustify}
        autoFlow={autoFlow}
      >
        {elements.map((element, index) => (
          <div key={index}>{element}</div>
        ))}
      </RepeaterContainer>
    </OuterContainer>
    {showBottomPagination && (
      <Paginator
        currentPage={currentPage}
        numberOfPages={numberOfPages}
        searchParams={searchParams}
      />
    )}
  </>
)
export const PaginationGrid = ({
  title,
  headingVariant,
  subHeading,
  elements,
  showBottomPagination = false,
  isSkeleton,
  totalPages,
  rows,
  gridClassName,
  gridJustify,
  elementWidth,
  autoFlow,
  headerProps,
  currentPage,
  searchParams,
}: PaginationListProps) => {
  if (elements.length === 0) {
    return (
      <OuterContainer>
        <ListHeader
          isSkeleton={isSkeleton}
          title={title}
          headingVariant={headingVariant}
          subHeading={subHeading}
        />
        <NoResults data-testid='no-results'>No Results!</NoResults>
      </OuterContainer>
    )
  }

  return (
    <>
      <Suspense>
        <SectionHeader
          seeAllLink={headerProps?.seeAllLink}
          seeAllTitle={headerProps?.seeAllTitle}
          type={headerProps?.type ?? 'empty'}
          defaultSortBy={headerProps?.defaultSortBy}
          showBuyNowOnly={headerProps?.showBuyNowOnly}
          enableBuyNowOnly={headerProps?.enableBuyNowOnly}
          title={headerProps?.title ?? ''}
          headingVariant={headerProps?.headingVariant}
          className={headerProps?.className}
        />
      </Suspense>
      <PaginationInnerGrid
        numberOfPages={totalPages}
        currentPage={currentPage}
        gridClassName={gridClassName}
        elementWidth={elementWidth}
        rows={rows}
        gridJustify={gridJustify}
        autoFlow={autoFlow}
        showBottomPagination={showBottomPagination}
        elements={elements}
        searchParams={searchParams}
      />
    </>
  )
}
